import { createElement, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Icon } from '@lounge-fe/ui-kit'

const SoundButton = ({ video }: { video: HTMLVideoElement }) => {
  const [isMuted, setIsMuted] = useState(video.muted)

  const toggleMute = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  useEffect(() => {
    const mute = () => setIsMuted(video.muted)
    mute()
    video.addEventListener('volumechange', mute)
    return () => video.removeEventListener('volumechange', mute)
  }, [video])

  return (
    <button
      onClick={toggleMute}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      {isMuted ? (
        <Icon.Mute style={{ color: 'white' }} />
      ) : (
        <Icon.Sound style={{ color: 'white' }} />
      )}
    </button>
  )
}

class VideoSoundButton extends HTMLElement {
  private shadow!: ShadowRoot
  private currentVideo: HTMLVideoElement | null = null
  private root: ReturnType<typeof createRoot> | null = null

  connectedCallback() {
    this.shadow = this.attachShadow({ mode: 'closed' })
    this.root = createRoot(this.shadow)

    const updateVideo = () => {
      const visibleVideo = Array.from(
        this.parentElement?.querySelectorAll('video') || [],
      ).find(
        (v) =>
          getComputedStyle(v).display !== 'none' && v.offsetParent !== null,
      )

      if (visibleVideo && visibleVideo !== this.currentVideo) {
        this.currentVideo = visibleVideo

        this.currentVideo.muted = true

        this.root?.render(
          createElement(SoundButton, {
            video: this.currentVideo,
            key:
              this.currentVideo.currentSrc ||
              this.currentVideo.src ||
              Math.random().toString(),
          }),
        )
      }
    }

    updateVideo()
    window.addEventListener('resize', updateVideo)
    this._disconnect = () => window.removeEventListener('resize', updateVideo)
  }

  disconnectedCallback() {
    this._disconnect?.()
  }

  private _disconnect?: () => void
}

customElements.define('video-sound-btn', VideoSoundButton)
